import { v4 as uuidv4 } from "uuid";
import type { ClassMember } from "../networking/schoolClasses";

export const COOKIE_NAME = "class_member_sid";
export const LOCAL_STORAGE_PREFIX = "class_member:";
const SESSION_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days, sliding window — refreshed on every successful session revalidation

// True if a `storage` event refers to (or could refer to) our entries:
// our own prefix, or a full localStorage clear (event.key === null).
export const isClassMemberStorageEvent = (e: StorageEvent): boolean =>
    e.key === null || e.key.startsWith(LOCAL_STORAGE_PREFIX);

export interface ClassMemberSession {
    sessionId: string;
    loginCode: string;
    member: ClassMember;
}

const _readSessionCookie = (): string | null => {
    const match = document.cookie.split("; ").find((row) => row.startsWith(`${COOKIE_NAME}=`));
    return match ? decodeURIComponent(match.substring(COOKIE_NAME.length + 1)) : null;
};

// Persistent cookie with Max-Age = SESSION_TTL_MS. Survives browser close.
// Sliding window: refreshed on every successful session revalidation via writeClassMemberSession.
const _setSessionCookie = (value: string, maxAgeSeconds: number) => {
    const isHttps = typeof window !== "undefined" && window.location.protocol === "https:";
    const secure = isHttps ? "; Secure" : "";
    document.cookie = `${COOKIE_NAME}=${value}; Path=/; Max-Age=${maxAgeSeconds}; SameSite=Lax${secure}`;
};

const _writeSessionCookie = (sessionId: string) =>
    _setSessionCookie(encodeURIComponent(sessionId), Math.floor(SESSION_TTL_MS / 1000));

const _clearSessionCookie = () => _setSessionCookie("", 0);

const _localStorageKey = (sessionId: string) => `${LOCAL_STORAGE_PREFIX}${sessionId}`;

// Removes every prefixed entry except `keepKey` (if provided).
const _removePrefixedLocalStorage = (keepKey?: string) => {
    try {
        for (let i = window.localStorage.length - 1; i >= 0; i--) {
            const key = window.localStorage.key(i);
            if (key && key.startsWith(LOCAL_STORAGE_PREFIX) && key !== keepKey) {
                window.localStorage.removeItem(key);
            }
        }
    } catch {
        // localStorage may be disabled (private mode); nothing to do.
    }
};

export const readClassMemberSession = (): ClassMemberSession | null => {
    const sessionId = _readSessionCookie();
    if (!sessionId) {
        // Cookie gone → orphaned localStorage (e.g. from a previous browser session).
        _removePrefixedLocalStorage();
        return null;
    }
    try {
        const raw = window.localStorage.getItem(_localStorageKey(sessionId));
        if (!raw) return null;
        const parsed = JSON.parse(raw) as { loginCode: string; member: ClassMember; expiresAt?: number };
        if (!parsed?.loginCode || !parsed?.member?.schoolClassId) return null;
        if (typeof parsed.expiresAt !== "number" || Date.now() >= parsed.expiresAt) {
            clearClassMemberSession();
            return null;
        }
        return { sessionId, loginCode: parsed.loginCode, member: parsed.member };
    } catch {
        return null;
    }
};

// crypto.randomUUID requires a secure context (https or localhost); fall back to uuidv4 on plain http.
const _generateSessionId = (): string =>
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function" ? crypto.randomUUID() : uuidv4();

export const writeClassMemberSession = (loginCode: string, member: ClassMember): ClassMemberSession => {
    const sessionId = _generateSessionId();
    const expiresAt = Date.now() + SESSION_TTL_MS;
    const newKey = _localStorageKey(sessionId);
    const newValue = JSON.stringify({ loginCode, member, expiresAt });

    // Order matters for cross-tab listeners: write the new entry FIRST, then point the cookie at it,
    // THEN remove old entries. Other tabs' `storage` listeners therefore never observe a moment of
    // null state during a session rotation. If we cleared first, listeners would briefly see no
    // session and could redirect / cascade unwanted logouts.
    try {
        window.localStorage.setItem(newKey, newValue);
    } catch {
        // localStorage unavailable: cookie alone won't satisfy persistence; let caller still get the session for in-memory use.
    }
    _writeSessionCookie(sessionId);
    _removePrefixedLocalStorage(newKey);
    return { sessionId, loginCode, member };
};

export const clearClassMemberSession = () => {
    _removePrefixedLocalStorage();
    _clearSessionCookie();
};

// Outside LOCAL_STORAGE_PREFIX on purpose: every session write removes the other prefixed entries.
const CODE_REMINDER_KEY = "class_member_code_reminder";

/** Remembers that `memberId` asked to see their code again after their next submitted test. */
export const rememberCodeReminder = (memberId: string) => {
    try {
        window.localStorage.setItem(CODE_REMINDER_KEY, memberId);
    } catch {
        // localStorage may be disabled (private mode); the reminder is a convenience only.
    }
};

export const isCodeReminderDue = (memberId: string): boolean => {
    try {
        return window.localStorage.getItem(CODE_REMINDER_KEY) === memberId;
    } catch {
        return false;
    }
};

export const clearCodeReminder = () => {
    try {
        window.localStorage.removeItem(CODE_REMINDER_KEY);
    } catch {
        // See rememberCodeReminder.
    }
};
